import BackButton from 'components/backButton'
import { Button, Dropdown, FormLayout, TextInput } from 'components/common'
import { useAuth, useSafeBack } from 'hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, UseFormReturn } from 'react-hook-form'
import { User } from 'types/user'
import {
  FRESH_MOBILE_ONLY_ROLES,
  FRESH_ROLES,
  PAGE_STORE_ORDERS,
  PAGE_USER_MGMT,
  PERMISSIONS,
  PROTECTED_PAGES,
  ROLES,
} from 'utils/constants'
import StoreDropdown from './storeDropdown'
import StoreAppDetails from './storeAppDetails'
import ConfirmRoleChangeModal from './confirmRoleChangeModal'
import { PAGE_ACCESS_KEY_PREFIX } from './utils'
import { fetchConfigurations } from 'utils/api/configApi'
import {
  getAllowedPagesByRole,
  getAssignableFreshCategories,
  RoleAllowedPages,
} from 'utils/api/userManagement'

interface UserFormProps {
  isUpdatePage?: boolean
  submitButtonText: string
  submitHandler: SubmitHandler<any>
  hookForm: UseFormReturn<any, any, undefined>
  defaultUser?: User
}

const UserForm = ({
  submitHandler,
  isUpdatePage = false,
  submitButtonText = 'Submit',
  hookForm,
  defaultUser,
}: UserFormProps) => {
  const { user } = useAuth()

  const [approverOptions, setApproverOptions] = useState([])

  // Backend-controlled subset of pages the selected role may be granted.
  // PICKER, FRESH_STAFF and FRESH_REPORT are all restricted, so this is looked
  // up per role rather than special-cased per role in the UI.
  const [roleAccess, setRoleAccess] = useState<RoleAllowedPages | null>(null)

  // Active Fresh categories, for the assignment checklist
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([])
  const [categoriesLoaded, setCategoriesLoaded] = useState<boolean>(false)
  // Distinguishes a failed request from "no categories exist" - the two
  // need very different messages
  const [categoriesFailed, setCategoriesFailed] = useState<boolean>(false)

  // Bumped to remount the page-access dropdowns so they reflect a bulk value change
  const [pageAccessResetKey, setPageAccessResetKey] = useState<number>(0)

  // Bumped to remount the role dropdown when a Super Admin change is cancelled
  const [roleResetKey, setRoleResetKey] = useState<number>(0)

  // Confirmation modal shown before switching a user's role to Super Admin
  const [isRoleConfirmOpen, setIsRoleConfirmOpen] = useState<boolean>(false)

  // Role to restore in the dropdown when a Super Admin change is cancelled
  const [revertRole, setRevertRole] = useState<string | undefined>(undefined)

  // The role selected before an (unconfirmed) switch to Super Admin
  const prevRoleRef = useRef<string | undefined>(undefined)

  const {
    reset,
    watch,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = hookForm

  const getApprovalManagers = async () => {
    try {
      const { data } = await fetchConfigurations({
        fields: ['approvalManagers'],
      })

      setApproverOptions(
        data?.approvalManagers.map((manager: any) => ({
          value: manager.id,
          label: manager.name,
        })) || [],
      )
    } catch (error) {
      setApproverOptions([])
    }
  }

  useEffect(() => {
    getApprovalManagers()
  }, [])

  useEffect(() => {
    if (defaultUser) {
      const pageAccessKeyValue: Record<string, boolean> = {}
      defaultUser.pageAccess?.forEach((page) => {
        pageAccessKeyValue[`PAGE_${page.page}`] = page.isEditor
      })
      reset({ ...defaultUser, ...pageAccessKeyValue })
    }
  }, [defaultUser])
  // to watch the change of role dropdown
  const role = watch('role')

  const permissions = (watch('permissions') as string[]) ?? []

  // Determine if the user is a `super admin` based on their role
  const isSuperAdmin = user.role === ROLES.SUPER_ADMIN

  // Fetch (and cache) the pages this role may hold whenever the role changes
  useEffect(() => {
    if (!role) return

    getAllowedPagesByRole(role)
      .then(setRoleAccess)
      .catch(() => setRoleAccess(null))
  }, [role])

  // Only trust roleAccess once it matches the currently selected role, so a
  // slow response for the previous role cannot filter the new role's pages.
  const activeRoleAccess =
    roleAccess && roleAccess.role === role ? roleAccess : null
  const isRestricted = activeRoleAccess?.isRestricted === true
  const allowedPages = isRestricted ? activeRoleAccess?.pages ?? null : null

  // A role allowed zero pages (FRESH_STAFF) is mobile-app only - the whole
  // Page Access panel is hidden for it.
  const holdsNoPages = isRestricted && allowedPages?.length === 0

  // Drop any selected page the role may not hold, so it is not sent on submit
  // (the backend rejects disallowed pages). For a zero-page role this clears
  // every page, which is what makes a converted user submit an empty list.
  useEffect(() => {
    if (!allowedPages) return

    PROTECTED_PAGES.forEach((page) => {
      if (!allowedPages.includes(page.id)) {
        setValue(`${PAGE_ACCESS_KEY_PREFIX}${page.id}`, null)
      }
    })

    // Remount the dropdowns so any cleared value is reflected on screen
    setPageAccessResetKey((key) => key + 1)
  }, [allowedPages, setValue])

  // options for Page access Permissions
  const pageAccessPermissionOptions = useMemo(() => {
    // initial all options
    let options = PROTECTED_PAGES

    if (isRestricted) {
      // A restricted role can only be granted its backend-controlled subset
      const allowed = allowedPages ?? []
      options = PROTECTED_PAGES.filter((page) => allowed.includes(page.id))
    } else if (!isSuperAdmin || role === ROLES.ADMIN) {
      // options for Admin user or dropdown selected for admin
      options = PROTECTED_PAGES.filter((page) => page.id !== PAGE_USER_MGMT.id)
    }

    return options.map((page) => ({
      label: page.name,
      value: page.id,
      disabled: false,
    }))
  }, [isSuperAdmin, role, isRestricted, allowedPages])

  // options for User roles
  const rolesOptions = useMemo(
    () => [
      { label: 'Admin', value: ROLES.ADMIN, disabled: !isSuperAdmin },
      {
        label: 'Super Admin',
        value: ROLES.SUPER_ADMIN,
        disabled: !isSuperAdmin,
      },
      { label: 'Picker', value: ROLES.PICKER, disabled: !isSuperAdmin },
      {
        label: 'Fresh Staff',
        value: ROLES.FRESH_STAFF,
        disabled: !isSuperAdmin,
      },
      {
        label: 'Fresh Report',
        value: ROLES.FRESH_REPORT,
        disabled: !isSuperAdmin,
      },
    ],
    [isSuperAdmin],
  )

  // Fired only on a user-driven role selection. `role` (closure) is the role
  // before this change; `value` is the newly picked role. Switching to Super
  // Admin replaces the current page access with Editor on all pages, so confirm
  // first (frontend-only — no API call).
  const handleRoleChange = (value: string | boolean | null) => {
    if (value === ROLES.SUPER_ADMIN && role !== ROLES.SUPER_ADMIN) {
      prevRoleRef.current = role
      setIsRoleConfirmOpen(true)
      return
    }

    // Switching to Admin clears all page access to No Access (e.g. after a
    // Super Admin change had granted Editor on every page).
    if (value === ROLES.ADMIN && role !== ROLES.ADMIN) {
      PROTECTED_PAGES.forEach((page) => {
        setValue(`${PAGE_ACCESS_KEY_PREFIX}${page.id}`, null)
      })
      setPageAccessResetKey((key) => key + 1)
    }
  }

  // Confirmed: grant editor access on every page and remount the page dropdowns
  // so they visually reflect the new Editor value.
  const handleConfirmRoleChange = () => {
    PROTECTED_PAGES.forEach((page) => {
      setValue(`${PAGE_ACCESS_KEY_PREFIX}${page.id}`, true)
    })
    setPageAccessResetKey((key) => key + 1)
    setIsRoleConfirmOpen(false)
  }

  // Cancelled: restore the previous role and remount the role dropdown so it
  // shows the reverted value. Page access is left untouched.
  const handleCancelRoleChange = () => {
    const prevRole = prevRoleRef.current
    setValue('role', prevRole ?? ROLES.ADMIN)
    setRevertRole(prevRole)
    setRoleResetKey((key) => key + 1)
    setIsRoleConfirmOpen(false)
  }

  const permissionOptions = [
    { label: 'No Access', value: null, disabled: false },
    { label: 'Editor', value: true, disabled: false },
    {
      label: 'Viewer',
      value: false,
      disabled: false,
    },
  ]

  const SAP_PERMISSION_OPTIONS = [
    {
      value: PERMISSIONS.STORE_SAP_CONFIG_EDIT,
      label: 'Store — Edit SAP Configuration',
      description: 'Allows editing the SAP Store Code on the Store update form',
    },
    {
      value: PERMISSIONS.AGGREGATOR_SAP_CONFIG_EDIT,
      label: 'Aggregator — Edit SAP Configuration',
      description:
        'Allows editing all SAP fields on the Aggregator update form',
    },
  ]

  // To return back to list
  const safeBackToList = useSafeBack(PAGE_USER_MGMT.path)
  const handleBack = () => {
    safeBackToList()
    reset()
  }

  // Only a Fresh Staff user holds categories. They scope what the stocker
  // sees in the mobile app, and FRESH_STAFF is the only role that can sign in
  // to it - a Fresh Admin manages categories on the admin site but cannot use
  // the app with that account.
  const canHoldFreshCategories = role === ROLES.FRESH_STAFF

  // A Fresh role reaches none of the GrandPOS or SAP settings below: the PIN
  // authorises a GrandPOS supervisor action, the three toggles are store-order
  // and price-label permissions, and the SAP permissions unlock admin config
  // sections no Fresh page has. Hidden so the form only asks what applies.
  const isFreshRole = FRESH_ROLES.includes(role)

  const freshCategories = (watch('freshCategories') as string[]) ?? []

  // Fetched once, the first time a category-capable role is selected
  useEffect(() => {
    if (!canHoldFreshCategories || categoriesLoaded) return

    getAssignableFreshCategories()
      .then((categories) => {
        setCategoriesFailed(false)
        setCategoryOptions(
          categories.map((category) => ({
            label: category.name,
            value: category._id,
          })),
        )
      })
      .catch(() => {
        setCategoriesFailed(true)
        setCategoryOptions([])
      })
      .finally(() => setCategoriesLoaded(true))
  }, [canHoldFreshCategories, categoriesLoaded])

  // Switching to a role that cannot hold categories, or taking Editor off
  // Fresh Category Management, drops any that were selected, so they are not
  // submitted for a user they no longer apply to.
  useEffect(() => {
    if (!canHoldFreshCategories && freshCategories.length) {
      setValue('freshCategories', [])
    }
  }, [canHoldFreshCategories, freshCategories.length, setValue])

  // Cleared rather than just hidden: validateUpdateUser requires the three
  // booleans, so a hidden-but-unregistered field would submit nothing and be
  // rejected. Clearing also strips grants carried over by a user converted
  // from an admin role to a Fresh one.
  useEffect(() => {
    if (!isFreshRole) return

    setValue('canCancelOrder', false)
    setValue('canGenerateVoidCompleteReport', false)
    setValue('canAccessPriceLabel', false)
    setValue('permissions', [])
    setValue('pinId', '')
  }, [isFreshRole, setValue])

  // A mobile-app-only role (Fresh Staff) never signs in to the admin site, so
  // the Approval Manager does not apply - but it must be tied to a store.
  const isFreshMobileOnly = FRESH_MOBILE_ONLY_ROLES.includes(role)

  // Store Order Page select watcher
  const storeOrderPage = watch(PAGE_ACCESS_KEY_PREFIX + PAGE_STORE_ORDERS.id)
  const showStoreDropdown =
    typeof storeOrderPage === 'boolean' || isFreshMobileOnly

  return (
    <section>
      <BackButton handleBack={handleBack} />
      <FormLayout>
        <form
          className="w-full max-w-4xl 2xl:max-w-4xl"
          onSubmit={handleSubmit(submitHandler)}
        >
          <div className="grid grid-cols-8 gap-10 md:divide-x-2">
            <div className="col-span-8 flex flex-col gap-2 md:col-span-3 ">
              <TextInput
                required={!isUpdatePage}
                label="First Name"
                register={register}
                name="firstName"
                placeholder="Enter your First Name"
                error={errors?.firstName?.message as string}
              />
              {/* Last Name */}
              <TextInput
                required={!isUpdatePage}
                label="Last Name"
                register={register}
                name="lastName"
                placeholder="Enter your Last Name"
                error={errors?.lastName?.message as string}
              />
              {/* Employee ID */}
              <TextInput
                required={!isUpdatePage}
                label="Employee ID"
                register={register}
                name="empId"
                placeholder="Enter your Employee ID"
                error={errors?.empId?.message as string}
                disabled={!!isUpdatePage}
              />
              {/* Email */}
              <TextInput
                required={!isUpdatePage}
                label="Email"
                register={register}
                name="email"
                placeholder="Enter your Email"
                error={errors?.email?.message as string}
                disabled={!!isUpdatePage}
              />
              {/* Password */}
              {isSuperAdmin && (
                <TextInput
                  required={!isUpdatePage}
                  label="Password"
                  register={register}
                  name="password"
                  type="password"
                  hintText="Atleast 8 characters, capital letter, small letter and symbol"
                  placeholder="Enter Password here"
                  error={errors?.password?.message as string}
                />
              )}
              {/* Role */}
              <Dropdown
                key={roleResetKey}
                label="Role"
                setValue={setValue}
                name="role"
                defaultValue={revertRole ?? defaultUser?.role}
                error={errors?.role?.message as string}
                options={rolesOptions}
                onChange={handleRoleChange}
              />
              {/* Account Status */}
              <Dropdown
                label="Account Status"
                setValue={setValue}
                name="isActive"
                options={[
                  { label: 'Active', value: true, disabled: false },
                  { label: 'Inactive', value: false, disabled: false },
                ]}
                defaultValue={defaultUser?.isActive}
                error={errors?.isActive?.message as string}
              />

              {/* Permissions */}
              {isSuperAdmin && !isFreshRole && (
                <div className="mt-4">
                  <p className="text-base font-bold text-sidebar-primary">
                    Permissions
                  </p>
                  <p className="mb-2 text-sm text-gray-500">
                    Grant access to protected configuration sections.
                  </p>
                  <div className="flex flex-col gap-2">
                    {SAP_PERMISSION_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        htmlFor={opt.value}
                        className="flex cursor-pointer items-start gap-3"
                      >
                        <input
                          type="checkbox"
                          id={opt.value}
                          className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600"
                          checked={permissions.includes(opt.value)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...permissions, opt.value]
                              : permissions.filter((p) => p !== opt.value)
                            setValue('permissions', updated)
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {opt.label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {opt.description}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Store Selector */}
              {showStoreDropdown && (
                <>
                  <StoreDropdown
                    withNoneOption
                    defaultValue={defaultUser?.store}
                    setValue={setValue}
                    error={errors?.store?.message as string}
                    label="Store"
                    name="store"
                    required
                    hintText={
                      isFreshMobileOnly
                        ? 'The store whose inventory this user updates in the Fresh mobile app'
                        : "The selected store's order will be displayed in the store order page"
                    }
                  />
                </>
              )}
              {/* Required to create a picker; view only afterwards */}
              {!isUpdatePage && role === ROLES.PICKER && (
                <TextInput
                  required
                  label="Picker ID (Order Tracker)"
                  register={register}
                  name="externalPickerId"
                  hintText="Picker's ID in Order Tracker. Must be unique."
                  error={errors?.externalPickerId?.message as string}
                />
              )}
              {canHoldFreshCategories && (
                <div className="mt-6">
                  <label className="text-base font-bold text-sidebar-primary">
                    Fresh Mobile App
                    {isFreshMobileOnly && <span className="text-error">*</span>}
                  </label>
                  <p className="mb-3 text-sm text-gray-500">
                    The categories this stocker can work through in the Fresh
                    mobile app.
                  </p>

                  {categoryOptions.length === 0 ? (
                    <p
                      className={`text-sm ${
                        categoriesFailed ? 'text-error' : 'text-gray-500'
                      }`}
                    >
                      {!categoriesLoaded
                        ? 'Loading categories...'
                        : categoriesFailed
                          ? 'Could not load Fresh categories. Reopen this page to try again.'
                          : 'No active Fresh categories exist yet. Create one in Fresh Category Management first.'}
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {categoryOptions.map((category) => (
                        <label
                          key={category.value}
                          htmlFor={`fresh-category-${category.value}`}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <input
                            type="checkbox"
                            id={`fresh-category-${category.value}`}
                            className="h-4 w-4 shrink-0 accent-blue-600"
                            checked={freshCategories.includes(category.value)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...freshCategories, category.value]
                                : freshCategories.filter(
                                    (id) => id !== category.value,
                                  )
                              setValue('freshCategories', updated)
                            }}
                          />
                          <span className="text-sm text-gray-900">
                            {category.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {errors?.freshCategories?.message && (
                    <p className="mt-2 text-sm text-error">
                      {errors.freshCategories.message as string}
                    </p>
                  )}
                </div>
              )}

              {!isFreshMobileOnly && (
                <Dropdown
                  required
                  label="Approval Manager"
                  setValue={setValue}
                  name="approverId"
                  options={approverOptions}
                  defaultValue={defaultUser?.approverId}
                  error={errors?.approverId?.message as string}
                />
              )}

              {/* Store app fields, grouped and view only */}
              {isUpdatePage && !isFreshRole && (
                <StoreAppDetails user={defaultUser} />
              )}
            </div>
            <div className="col-span-8 w-full md:col-span-4 md:pl-7">
              <label className="text-base font-bold text-sidebar-primary">
                Page Access Permission
                {!isUpdatePage && !holdsNoPages && (
                  <span className="text-error">*</span>
                )}
              </label>

              {holdsNoPages ? (
                // A role allowed zero admin pages (Fresh Staff) uses the Fresh
                // mobile app only. Any page selected before the role change is
                // cleared above, so nothing is submitted for this user.
                <p className="mt-3 text-sm text-gray-500">
                  This role has no admin site access. The user signs in to the
                  Fresh mobile app only, so no page access is assigned.
                </p>
              ) : (
                <div className="mt-3 flex w-full flex-col gap-2">
                  {pageAccessPermissionOptions.map((permission) => (
                    <div
                      className="flex w-full items-center justify-between gap-2"
                      key={`${permission.value}-${pageAccessResetKey}`}
                    >
                      <p className="shrink-0 whitespace-nowrap text-sm">
                        {permission.label}
                      </p>
                      <div className="w-full max-w-36">
                        <Dropdown
                          className="h-8 text-sm"
                          size="small"
                          setValue={setValue}
                          name={`PAGE_${permission.value}`}
                          defaultValue={getValues(`PAGE_${permission.value}`)}
                          error={errors?.role?.message as string}
                          options={permissionOptions}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions : submit | cancle */}
          <div className="mt-6 flex w-72 gap-x-4">
            <Button
              type="submit"
              buttonText={submitButtonText}
              className="w-full"
            />

            <Button
              type="button"
              className="w-full"
              variant="outline"
              buttonText={'Cancel'}
              onClick={handleBack}
            />
          </div>
        </form>
      </FormLayout>

      <ConfirmRoleChangeModal
        isOpen={isRoleConfirmOpen}
        onConfirm={handleConfirmRoleChange}
        onCancel={handleCancelRoleChange}
      />
    </section>
  )
}

export default UserForm
